/**
 * Language Variant Storage Service
 * 
 * Unified storage for language variant preference.
 * Migrates old storage formats to new unified format.
 */

import { LanguageVariant, LanguageVariantPreference } from './languageVariants.types';

const STORAGE_KEY = 'language_variant_preference';
const OLD_STORAGE_KEY = 'languageVariant';

/**
 * Migrate old storage formats to new format
 */
function migrateOldStorage(): LanguageVariant | null {
  try {
    // Check old format: 'languageVariant' (plain string, Dutch)
    const oldString = localStorage.getItem(OLD_STORAGE_KEY);
    if (oldString) {
      // Map Dutch to English
      const mapping: Record<string, LanguageVariant> = {
        'praktisch': 'practical',
        'neutraal': 'neutral',
        'reflectief': 'reflective'
      };
      
      const variant = mapping[oldString.toLowerCase()];
      if (variant) {
        // Save in new format
        saveLanguageVariant(variant);
        // Remove old key
        localStorage.removeItem(OLD_STORAGE_KEY);
        return variant;
      }
    }
  } catch (err) {
    console.warn('Failed to migrate old language variant storage:', err);
  }
  return null;
}

/**
 * Load language variant from localStorage
 * @returns The stored language variant or 'neutral' as default
 */
export function loadLanguageVariant(): LanguageVariant {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const preference: LanguageVariantPreference = JSON.parse(stored);
      if (preference.variant && ['practical', 'neutral', 'reflective'].includes(preference.variant)) {
        return preference.variant;
      }
    }
    
    // Try migration from old formats
    const migrated = migrateOldStorage();
    if (migrated) {
      return migrated;
    }
  } catch (err) {
    console.warn('Failed to load language variant:', err);
  }
  return 'neutral'; // Default
}

/**
 * Save language variant to localStorage
 * @param variant The language variant to save
 */
export function saveLanguageVariant(variant: LanguageVariant): void {
  try {
    const preference: LanguageVariantPreference = {
      variant,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preference));
  } catch (err) {
    console.error('Failed to save language variant:', err);
  }
}
